import { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRangeFilterDefinition } from '@asset-metrix/portal-gateway-client-js';
import { DateRange, DateRangePicker } from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import { useStore } from '../../store';
import { toDateString } from '../../utils/dateHelper';

export type RangeDatePickerFilterProps = {
  dateTimeFormatOptions?: Intl.DateTimeFormatOptions;
  entityId: string;
  filterData?: DateRangeFilterDefinition;
  filterKey: string;
  isFetching?: boolean;
  disabled?: boolean;
  label?: ReactNode | ReactNode[];
};

function filterDataToDateRange(
  filterDefinition?: DateRangeFilterDefinition
): DateRange {
  if (filterDefinition?.selection == null) {
    return { start: undefined, end: undefined };
  }
  const { selection } = filterDefinition;
  return {
    start: selection.start != null ? new Date(selection.start) : undefined,
    end: selection.end != null ? new Date(selection.end) : undefined,
  };
}

export function RangeDatePickerFilter({
  dateTimeFormatOptions,
  entityId,
  disabled,
  filterData,
  filterKey,
  isFetching,
  label,
}: RangeDatePickerFilterProps) {
  const { t } = useTranslation();
  const [setFilterSelection, resetFilterSelection] = useStore(
    useShallow((state) => [
      state.setFilterSelection,
      state.resetFilterSelection,
    ])
  );

  const [value, setValue] = useState(filterDataToDateRange(filterData));

  useEffect(() => {
    setValue(filterDataToDateRange(filterData));
  }, [filterData]);

  return (
    <DateRangePicker
      dateTimeFormatOptions={
        // omit time constraints
        dateTimeFormatOptions
          ? {
              ...dateTimeFormatOptions,
              hour: undefined,
              minute: undefined,
            }
          : undefined
      }
      label={label}
      value={value}
      loading={isFetching}
      disabled={disabled}
      onChange={setValue}
      placeholderFrom={t('app.pageFilter.dateRangeFilter.placeholderFrom')}
      placeholderTo={t('app.pageFilter.dateRangeFilter.placeholderTo')}
      onCommit={(range) => {
        if (range.start == null && range.end == null) {
          resetFilterSelection(entityId, filterKey);
        } else {
          setFilterSelection(entityId, filterKey, 'date', 'range-date-picker', {
            start: range.start ? toDateString(range.start) : undefined,
            end: range.end ? toDateString(range.end) : undefined,
          });
        }
      }}
    />
  );
}
